import { Component } from '@theme/component';
import { supportsViewTransitions, startViewTransition, isLowPowerDevice } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a lightbox dialog for product media.
 *
 * The dialog frames a single piece of media at a time, just inside the viewport edges, and the
 * visitor moves between the rest of the product's media with the arrow buttons or the left/right
 * arrow keys. Clicking anywhere outside the media closes it.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements, one per product media item.
 * @property {HTMLElement} [counter] - The "n / total" position indicator.
 * @property {HTMLElement} [previous] - The previous arrow button.
 * @property {HTMLElement} [next] - The next arrow button.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #activeIndex = 0;
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    const { signal } = this.#controller;

    dialog.addEventListener('click', this.#handleDialogClick, { signal });
    dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #count() {
    return this.refs.media.length;
  }

  /**
   * Opens the dialog on a specific media item.
   *
   * @param {number} index - The index of the media to show.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    // Select before showing so the dialog opens on the media that was clicked rather than
    // flashing the previously active one.
    this.#select(index);

    const open = () => dialog.showModal();

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Closes the dialog, transitioning the active media back into the gallery where possible.
   */
  async close() {
    const { media } = this.refs;
    const activeMedia = media[this.#activeIndex];

    if (!supportsViewTransitions() || isLowPowerDevice() || !activeMedia) return this.closeDialog();

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[this.#activeIndex]
      : mediaGallery?.media?.[this.#activeIndex];

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    activeMedia.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      activeMedia.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Moves to the next media item, wrapping around at the end.
   */
  next() {
    this.#select(this.#activeIndex + 1);
  }

  /**
   * Moves to the previous media item, wrapping around at the start.
   */
  previous() {
    this.#select(this.#activeIndex - 1);
  }

  /**
   * Handles escape to close and the arrow keys to move between media.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
    }
  }

  /**
   * Starts loading the image for a given index, so that arrowing onto it doesn't wait on the network.
   *
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const image = this.refs.media[index]?.querySelector('img.product-media__image');

    if (image instanceof HTMLImageElement && image.loading === 'lazy') {
      // Switching from lazy to eager starts the fetch even while the media is hidden.
      image.loading = 'eager';
    }
  }

  /**
   * Shows a single media item and hides the rest.
   *
   * @param {number} index - The index to show. Out of range values wrap around.
   */
  #select(index) {
    const { media, counter } = this.refs;
    const count = this.#count;

    if (!count) return;

    const activeIndex = ((index % count) + count) % count;
    this.#activeIndex = activeIndex;

    for (const [i, element] of media.entries()) {
      element.toggleAttribute('data-active', i === activeIndex);
      element.setAttribute('aria-hidden', `${i !== activeIndex}`);
    }

    // The active image plus its neighbours, so the arrows feel instant.
    for (const offset of [0, 1, -1]) {
      this.preloadImage((((activeIndex + offset) % count) + count) % count);
    }

    if (counter) counter.textContent = `${activeIndex + 1} / ${count}`;

    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }

  /**
   * Closes the dialog when the visitor clicks the backdrop or the empty space framing the media.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleDialogClick = (event) => {
    const { target } = event;

    if (!(target instanceof Element)) return;
    if (target.closest('button, a, img, video, iframe, model-viewer, product-model, deferred-media')) return;

    this.close();
  };

  /** @type {{ x: number, y: number } | null} */
  #touchStart = null;

  /**
   * @param {TouchEvent} event - The touch event.
   */
  #handleTouchStart = (event) => {
    const touch = event.touches.length === 1 ? event.touches[0] : null;

    this.#touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /**
   * Swiping horizontally moves between media, mirroring what the arrows do on larger screens.
   *
   * @param {TouchEvent} event - The touch event.
   */
  #handleTouchEnd = (event) => {
    const start = this.#touchStart;
    const touch = event.changedTouches[0];

    this.#touchStart = null;

    if (!start || !touch || this.#count < 2) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    // Ignore vertical swipes and taps.
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    deltaX < 0 ? this.next() : this.previous();
  };
}

/** Minimum horizontal travel, in pixels, before a touch counts as a swipe. */
const SWIPE_THRESHOLD = 40;

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
